import classNames from 'classnames';
import { kebabCase } from 'lodash';
import React, { useCallback, useState } from 'react';
import PercentageInput from 'src/components/Shared/PercentageInput/PercentageInput';
import { FederalTaxPercent } from 'src/settings/settings';
import { HelpButton } from '../Shared/HelpButton';
import styles from './styles.module.scss';
import './styles.scss';

export interface EditableDeductionListItemProps {
  federalWithholdingApplied?: boolean;
  federalWithholdingDollars?: number;
  federalWithholdingPercent?: number;
  onClickCheckbox(checked: boolean, minTax: FederalTaxPercent): void;
  onClickHelp: CallableFunction;
  onSave(fedVal: number): void;
  title: JSX.Element | string;
  ticker: string;
}

export const EditableDeductionListItem: React.FC<
  EditableDeductionListItemProps
> = ({
  children,
  onSave,
  title,
  onClickHelp,
  ticker,
  federalWithholdingPercent = 10,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [hasError, setError] = useState(false);
  const [federalValue, setFederalValue] = useState(federalWithholdingPercent);
  const saveFederalEdits = useCallback(
    (num) => {
      onSave(num);
      setError(num > FederalTaxPercent.MAX);
      setIsEditing(false);
    },
    [onSave]
  );

  const cacheFederalEdits = useCallback(
    (_editingFederalValue: string) => {
      const editingFederalValue = String(_editingFederalValue).replace(
        /[^0-9]/gm,
        ''
      );
      const editingFederalNum = parseInt(editingFederalValue, 10);

      setFederalValue(editingFederalNum);
      saveFederalEdits(editingFederalNum);
    },
    [saveFederalEdits]
  );

  return (
    <div
      className={classNames(styles['deduction-list-line'], {
        [styles.editing]: isEditing,
        // [styles.decline]: !federalWithholdingApplied,
      })}
    >
      {
        <React.Fragment>
          <PercentageInput
            autoFocus
            displayInline
            ariaLabel={`Federal withholding percentage between ${FederalTaxPercent.MIN} and ${FederalTaxPercent.MAX}`}
            containerClassName="edli-percent-input-container"
            defaultValue={federalValue}
            displayErrorMessage={false}
            error={hasError}
            id="federal-withholding-percent-input"
            inputClassName="edli-percent-input"
            placeholder={FederalTaxPercent.MIN}
            size={6}
            wrapperClassName="edli-percent-input-wrapper"
            onChange={(event) => {
              cacheFederalEdits(event);
            }}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^0-9]*/g, '');
            }}
          />
          <label
            className={classNames(
              styles['deduction-sub-label'],
              styles['editing-federal-label']
            )}
            htmlFor="federal-withholding-percent-input"
          >
            <div>
              {title}{' '}
              <HelpButton
                className={classNames(
                  `e2e-federal-withholding-info-link-${kebabCase(ticker)}`,
                  styles['deduction-list-federal-withholding-info']
                )}
                id="retirement-federal-withholding-info"
                onClick={() => onClickHelp()}
              >
                Open federal withholding modal
              </HelpButton>
              <br />
            </div>
            <span
              className={classNames(styles['editing-federal-note'], {
                [styles.error]: hasError,
              })}
            >
              Edit this field if you would like a rate of withholding that is
              different from the default withholding rate. See the instructions
              on page 2 of the IRS{' '}
              <a
                href="https://www.irs.gov/pub/irs-pdf/fw4r.pdf"
                rel="noreferrer"
                target="_blank"
              >
                Form W-4R{' '}
              </a>{' '}
              and the Marginal Rate Tables for additional information. Enter the
              rate as a whole number (no decimals). The following link will take
              you to the{' '}
              <a
                href={`https://www.irs.gov/pub/irs-pdf/fw4r.pdf`}
                rel="noreferrer"
                target="_blank"
              >
                {new Date().getFullYear()} Marginal Rate Tables
              </a>
              . You may use these tables to help you select the appropriate
              withholding rate for this payment or distribution. Instructions on
              how to best use them are included.
              {/* Must be between {FederalTaxPercent.MIN}&ndash;
              {FederalTaxPercent.MAX}% or 0% */}
            </span>
          </label>
        </React.Fragment>
      }
      {children}
    </div>
  );
};
